<template>
  <div class="supported-networks app__block">
    <h1>
      {{ 'supported-networks.header' | globalize }}

      <div class="pull-right">
        <permission-wrapper
          skip-alert
          page-name="supportedNetworks"
          resource-name="supportedNetwork"
          action-name="create">
          <supported-network-editor
            mode="new"
            :title="'supported-network-editor.btn-add' | globalize"
            :network="defaulrSupportedNetwork"
            :on-add="onEditNetwork"
            :loading="updating"
          />
        </permission-wrapper>
      </div>
    </h1>

    <template v-if="isLoaded && data.length && !isFailed">
      <a-list
        class="supported-network-list"
        :loading="isPending"
        bordered
        item-layout="vertical"
        :data-source="data"
      >
        <a-list-item
          slot="renderItem"
          slot-scope="item">
          <a-list-item-meta>
            <div slot="title">
              {{ item.name }}
              <permission-wrapper
                skip-alert
                page-name="supportedNetworks"
                resource-name="supportedNetwork"
                action-name="update">
                <supported-network-editor
                  :title="'supported-network-editor.btn-edit' | globalize"
                  mode="edit"
                  :network="item"
                  :on-add="onEditNetwork"
                  :loading="updating"
                />
              </permission-wrapper>

              <permission-wrapper
                skip-alert
                page-name="supportedNetworks"
                resource-name="supportedNetwork"
                action-name="delete">
                <delete-supported-network :on-click="() => onRemoveNetwork(item)" />
              </permission-wrapper>
            </div>
          </a-list-item-meta>

          <a-row>
            <a-col span="24">
              <div>
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-chain-id' | globalize }}
                </span>
                <span class="supported-networks-value">
                  <template v-if="item.editable">
                    editable - {{ item.chainId }}
                  </template>
                  <template v-else>
                    {{ item.chainId }}
                  </template>
                </span>
              </div>
              <div>
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-network-id' | globalize }}
                </span>
                <span class="supported-networks-value">
                  {{ item.networkId }}
                </span>
              </div>

              <div>
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-network' | globalize }}
                </span>
                <span class="supported-networks-value">
                  {{ item.network }}
                </span>
              </div>

              <div>
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-min-owner-balance' | globalize }}
                </span>
                <span class="supported-networks-value">
                  {{ item.minOwnerBalance }}
                </span>
              </div>

              <div class="supported-networks-actions">
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-rpc' | globalize }}
                </span>
                <span class="supported-networks-value">
                  {{ item.rpc }}
                </span>
              </div>

              <div class="supported-networks-actions">
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-wssRpc' | globalize }}
                </span>
                <span class="supported-networks-value">
                  {{ item.wssRpc }}
                </span>
              </div>

              <div class="supported-networks-actions">
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-subgraph-url' | globalize }}
                </span>
                <span class="pull-right">
                  <a
                    class="pull-right"
                    target="_blank"
                    :href="item.subgraphUrl">{{ item.subgraphUrl }}</a>
                </span>
              </div>

              <div class="supported-networks-actions">
                <span class="supported-networks-label">
                  {{ 'supported-networks.lbl-explorer-url' | globalize }}
                </span>
                <span class="supported-networks-value">
                  <a
                    target="_blank"
                    :href="item.explorerUrl">{{ item.explorerUrl }}</a>
                </span>
              </div>

              <supported-network-addresses
                :data="item.addresses"
                :explorer-url="item.explorerUrl"
              />
            </a-col>
          </a-row>

          <span slot="actions" />
        </a-list-item>
      </a-list>
    </template>
    <template v-else>
      <div class="app-list__li-like">
        <template v-if="isLoaded && !data.length">
          <p>
            {{ "verifications-list.nothing-here-yet" | globalize }}
          </p>
        </template>

        <template v-else-if="isFailed">
          <p>
            {{ "verifications-list.default-error-message" | globalize }}
          </p>
        </template>

        <template v-else>
          <p>
            {{ "verifications-list.loading-message" | globalize }}
          </p>
        </template>
      </div>
    </template>

    <collection-loader
      class="app__more-btn-wrp"
      :first-page-loader="getData"
      @first-page-load="setList"
      @next-page-load="concatList"
      ref="collectionLoaderBtn"
    />
  </div>
</template>

<script>

import ARow from 'ant-design-vue/lib/row'
import ACol from 'ant-design-vue/lib/col'
import AList from 'ant-design-vue/lib/list'
import { api } from '@/api'
import { ErrorHandler } from '@/js/helpers/error-handler'
import CollectionLoader from '@/vue/common/CollectionLoader'
import SupportedNetworkAddresses from '@/vue/common/supported-network/addresses'
import { addParamsToLocation } from '@/js/helpers/router-query'
import { SupportedNetworkRecord } from '@/js/records/entities/supported-network.record'
import SupportedNetworkEditor from '@/vue/common/supported-network/editor'
import DeleteSupportedNetwork from '@/vue/common/supported-network/delete'
import { Bus } from '@/js/helpers/event-bus'
import PermissionWrapper from '@/vue/common/permission-wrapper'

const defaulrSupportedNetwork = new SupportedNetworkRecord({})

export default {
  name: 'supported-networks-list',
  components: {
    CollectionLoader,
    ARow,
    ACol,
    AList,
    'a-list-item': AList.Item,
    'a-list-item-meta': AList.Item.Meta,
    SupportedNetworkAddresses,
    SupportedNetworkEditor,
    DeleteSupportedNetwork,
    PermissionWrapper,
  },
  data () {
    return {
      updating: false,
      showFilters: true,
      isLoaded: false,
      isFailed: false,
      isPending: false,
      filterGutter: { xs: 1, sm: 1, md: 2, lg: 2 },
      data: [],
      addresses: [],
      defaulrSupportedNetwork: defaulrSupportedNetwork,
    }
  },
  watch: {
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
  },
  async created () {
    await this.getData()
  },
  methods: {
    async getData () {
      this.isLoaded = false
      this.isFailed = false
      this.isPending = true
      let response = {}
      try {
        const query = {}

        response = await api.getWithSignature('/supported-networks', query)

        Bus.emit('supported-network-list-updated', response.data)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isLoaded = true
        this.isPending = false
      }

      return response
    },

    setList (data) {
      this.data = data.map(row => {
        const result = new SupportedNetworkRecord(row)
        return result
      })
    },

    concatList (data) {
      this.data = this.data.concat(
        data.map((i) => new SupportedNetworkRecord(i))
      )
    },

    reloadList () {
      addParamsToLocation(this.$route, this.filters)
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    toggleEditable (item) {
      item.editable = !item.editable
    },

    async onRemoveNetwork (network) {
      this.isPending = true
      this.isFailed = false

      try {
        await api.deleteWithSignature(`/supported-networks/${network.chainId}`)

        const { data } = await this.getData()

        this.data = data.map(row => {
          const result = new SupportedNetworkRecord(row)
          return result
        })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isPending = false
      }
    },

    async onEditNetwork (network, mode) {
      this.updating = true
      this.isFailed = false
      let body = {
        data: {
          type: 'add_supported_network',
          attributes: {
            name: network.name,
            network: network.network,
            network_id: parseInt(network.networkId, 10),
            chain_id: parseInt(network.chainId, 10),
            rpc: network.rpc,
            wss_rpc: network.wssRpc,
            subgraph_url: network.subgraphUrl,
            explorer_url: network.explorerUrl,
            min_owner_balance: parseFloat(network.minOwnerBalance, 10),
            cpk_factory_address: network.cpkFactoryAddress,
            smt_distributor_contract: network.smtDistributorContract,
            permission_manager_address: network.permissionManagerAddress,
            action_manager_address: network.actionManagerAddress,
            offer_listener_address: network.offerListenerAddress,
            asset_token_address: network.assetTokenAddress,
            bpool_proxy_address: network.bpoolProxyAddress,
          },
        },
      }

      let method = 'postWithSignature'
      let url = '/supported-networks'
      if (mode !== 'new') {
        body.data.type = 'supported_network'
        method = 'putWithSignature'
        url = `/supported-networks/${network.chainId}`
      }
      try {
        const bodyAsString = JSON.stringify(body)

        await api[method](url, bodyAsString)

        const { data } = await this.getData()

        this.data = data.map(row => {
          const result = new SupportedNetworkRecord(row)
          return result
        })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.updating = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '~ant-design-vue/lib/grid/style/index.css';
@import '~ant-design-vue/lib/list/style/index.css';
@import '~ant-design-vue/lib/card/style/index.css';

.ant-list-item-action li {
  width: 100% ;
}

.supported-networks-actions {
  text-align: justify;
}

.supported-networks-label {
  padding-right: 0.08em;
}

.supported-networks-value,
.supported-networks-value span span a {
  margin-right: 0.5em;
  float: right;
}

</style>
